/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import useFormPersist from "react-hook-form-persist";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../../API/axios";
import Layout from "../../Components/Layout";
import { Metatags, Page } from "../../types";

interface PageType {
  Id: number;
  Status_Name: string;
}

interface Category {
  Category_Id: number;
  Category_Name: string;
}

const CreateMeta = () => {
  const navigate = useNavigate();
  const [pagesType, setPagesType] = useState<PageType[]>([]);
  const [fetchedOptions, setFetchedOptions] = useState<any>([]);
  const [selectedPageType, setSelectedPageType] = useState<
    string | number | null
  >(null); // Track selected page type

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Metatags>();

  useFormPersist("metaTags", {
    watch,
    setValue,
    timeout: 1000 * 60 * 15,
    storage: window.localStorage,
  });

  const onSubmit: SubmitHandler<Metatags> = async (data) => {
    try {
      const response = await AxiosInstance.post(
        "/admin/web-content/v1/add-metadata",
        data
      );
      if (response.status === 200) {
        toast.success("Meta tags created successfully");
        window.localStorage.removeItem("metaTags");
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      console.error("Error creating meta tags:", error);
    }
  };

  const fetchPageType = async () => {
    try {
      const response = await AxiosInstance.get(
        "/admin/web-content/v1/get-page-options"
      );
      const data: PageType[] = response.data;
      setPagesType(data);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  const fetchMainPages = async () => {
    try {
      const response = await AxiosInstance.get(
        "/admin/modules/v1/get-all-pages-without-pagination"
      );
      setFetchedOptions(response.data);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  const fetchCategoryPages = async () => {
    try {
      const response = await AxiosInstance.get("/web/v1/categories");
      setFetchedOptions(response.data);
    } catch (error) {
      console.error("Error fetching pages:", error);
    }
  };

  useEffect(() => {
    fetchPageType();
  }, []);

  useEffect(() => {
    const fetchPages = async () => {
      if (selectedPageType !== null) {
        if (selectedPageType === 10) {
          await fetchMainPages();
        } else if (selectedPageType === 11) {
          await fetchCategoryPages();
        }
      }
    };

    fetchPages();
  }, [selectedPageType]);

  return (
    <Layout PageId={12}>
      <div className="flex flex-col items-start justify-start w-full h-full mt-4 rounded-md">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col items-start w-full justify-normal"
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Select page type</InputLabel>
                <Controller
                  name="page_type_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      size="small"
                      margin="none"
                      label="Select page type"
                      inputProps={{ "aria-label": "Without label" }}
                      value={field.value}
                      error={Boolean(errors.page_type_id)}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        setSelectedPageType(selectedValue as number);
                        field.onChange(e);
                      }}
                    >
                      {pagesType.map((page: PageType) => (
                        <MenuItem value={page.Id} key={page.Id}>
                          {page.Status_Name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">
                  Select page
                </InputLabel>
                <Controller
                  name="ref_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      size="small"
                      margin="none"
                      label="Page"
                      inputProps={{ "aria-label": "Without label" }}
                      {...field}
                    >
                      {selectedPageType === 10
                        ? fetchedOptions.map((page: Page) => (
                            <MenuItem value={page.Page_Id} key={page.Page_Id}>
                              {page.Page_Name}
                            </MenuItem>
                          ))
                        : fetchedOptions.map((page: Category) => (
                            <MenuItem
                              key={page.Category_Id}
                              value={page.Category_Id}
                            >
                              {page.Category_Name}
                            </MenuItem>
                          ))}
                    </Select>
                  )}
                />
                {errors.page_type_id && (
                  <p className="text-xs text-red-500">This field is required</p>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="meta_title"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    type="text"
                    size="small"
                    fullWidth
                    error={Boolean(errors.meta_title)}
                    label="Meta title"
                    className="w-full border rounded-md border-primary"
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <div className="relative">
                <Controller
                  name="meta_description"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative">
                      <TextField
                        type="text"
                        fullWidth
                        size="small"
                        label="Meta description"
                        className="w-full border rounded-md border-primary"
                        error={Boolean(errors.meta_description)}
                        {...field}
                      />
                    </div>
                  )}
                />
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className="relative">
                <Controller
                  name="meta_keywords"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative">
                      <TextField
                        type="text"
                        fullWidth
                        size="small"
                        label="Meta keywords"
                        error={Boolean(errors.meta_keywords)}
                        className="w-full border rounded-md border-primary"
                        {...field}
                      />
                    </div>
                  )}
                />
              </div>
            </Grid>
          </Grid>
        </form>

        <div className="flex flex-row items-center justify-between w-full mt-4 mb-4">
          <Button
            variant="contained"
            color="primary"
            sx={{
              color: "#fff",
            }}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/web/meta-tags/manage")}
            sx={{
              color: "#00A99D",
            }}
          >
            Back
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default CreateMeta;
